import type { ZodType } from "zod";
import z from "zod";

import { looseUuid } from "@thalamos/common";

import {
  OrganisationIdSchema,
  type OrganisationIdUnbranded,
} from "../organisation.js";
import { RoleSchema, UserIdSchema, type UserIdUnbranded } from "../user.js";
import {
  AuditTrailEntryKindSchema,
  type AuditTrailEntryKind,
} from "./AuditTrailEntryKind.js";
import {
  AuditTrailResourceKindSchema,
  AuditTrailResourceSchema,
  type AuditTrailResourceUnbranded,
} from "./resource.js";

export type AuditTrailEntryIdUnbranded = string;
export const AuditTrailEntryIdSchema =
  looseUuid().brand<"haven-common:AuditTrailEntryId">() satisfies ZodType<AuditTrailEntryIdUnbranded>;
/**
 * One recorded action in the audit trail. Branded because an entry id and the
 * id of the thing an entry is about are both UUIDs and are passed to adjacent
 * arguments.
 */
export type AuditTrailEntryId = z.infer<typeof AuditTrailEntryIdSchema>;

/**
 * One recorded action: what happened, who did it, in what session context, and
 * when.
 *
 * The reading of an entry is deliberately forgiving of everything except its
 * own envelope. `data` is read as a plain object and narrowed on demand with
 * `tryGetAuditData`, so a payload written by an older build can never fail the
 * read of a page of entries — while the fields the trail is searched and
 * attributed by are validated, because a row that cannot say who acted is not
 * an audit row.
 *
 * The context fields are denormalised on purpose. An organisation renamed after
 * the event, or a membership since revoked, must not change what the trail says
 * was true at the time.
 */
export type AuditTrailEntryUnbranded = {
  id: AuditTrailEntryIdUnbranded;
  timestamp: Date;

  kind: AuditTrailEntryKind;
  data: Record<string, unknown>;

  userId?: UserIdUnbranded | null;
  userName?: string | null;

  organisationId?: OrganisationIdUnbranded | null;
  organisationName?: string | null;
  roles?: string[] | null;

  parentTraceId?: string | null;
  /** A one-way digest of the session the action belonged to, never the key. */
  sessionHash?: string | null;
  ipAddress?: string | null;
  codeVersion: string;

  resources: AuditTrailResourceUnbranded[];
};

export const AuditTrailEntrySchema = z
  .object({
    id: AuditTrailEntryIdSchema,
    timestamp: z.coerce.date(),

    kind: AuditTrailEntryKindSchema,
    data: z.record(z.string(), z.unknown()),

    userId: UserIdSchema.nullish(),
    userName: z.string().nullish(),

    organisationId: OrganisationIdSchema.nullish(),
    organisationName: z.string().nullish(),
    // Plain strings rather than `RoleSchema`. The set of roles is closed in the
    // application and stored rows are not: a role retired from the product
    // still happened, and reading its entries must not depend on it still being
    // offered.
    roles: z.array(z.string()).nullish(),

    parentTraceId: z.string().nullish(),
    sessionHash: z.string().nullish(),
    ipAddress: z.string().nullish(),
    codeVersion: z.string(),

    resources: z.array(AuditTrailResourceSchema),
  })
  .brand<"haven-common:AuditTrailEntry">() satisfies ZodType<AuditTrailEntryUnbranded>;

export type AuditTrailEntry = z.infer<typeof AuditTrailEntrySchema>;

export const NewAuditTrailEntrySchema = z
  .object({
    id: AuditTrailEntryIdSchema.optional(),
    timestamp: z.coerce.date().optional(),

    kind: AuditTrailEntryKindSchema,
    data: z.record(z.string(), z.unknown()),

    userId: UserIdSchema.nullish(),
    userName: z.string().max(255).nullish(),

    organisationId: OrganisationIdSchema.nullish(),
    organisationName: z.string().max(255).nullish(),
    roles: z.array(RoleSchema).nullish(),

    parentTraceId: z.string().max(255).nullish(),
    // Exactly the width of the digest, which is also the width of the column. A
    // value of any other length is not one of ours, and saying so here names the
    // field rather than failing as a constraint violation on insert.
    sessionHash: z.string().length(64).nullish(),
    ipAddress: z.string().max(255).nullish(),

    resources: z.array(AuditTrailResourceSchema),
  })
  .brand<"haven-common:NewAuditTrailEntry">();

/**
 * An entry as a writer states it.
 *
 * `codeVersion` is absent because it is a fact about the recording rather than
 * about the action, so the repository stamps it from the running build — a
 * caller could otherwise record the wrong one, and the field exists to say which
 * code produced the row. `id` and `timestamp` are optional and generated when
 * omitted: a caller states a `timestamp` to record an action that happened
 * earlier, and an `id` to keep hold of the entry it is writing.
 *
 * `data` is loose here and checked against the entry kind's registered schema as
 * the write happens, which is where a mistyped payload is refused.
 */
export type NewAuditTrailEntry = z.infer<typeof NewAuditTrailEntrySchema>;

/** A bare calendar date, as against an instant. */
const dateOnly = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The end of the named day for a date, and the instant itself for anything else.
 *
 * Preprocessed to a string rather than adjusting the parsed `Date`, so the
 * distinction is drawn on what the caller actually wrote: once coerced, a date
 * and that date's midnight instant are the same value and cannot be told apart.
 */
const inclusiveUpperBound = z.preprocess(
  (value) =>
    typeof value === "string" && dateOnly.test(value)
      ? `${value}T23:59:59.999Z`
      : value,
  z.coerce.date(),
);

export const AuditTrailSearchSchema = z
  .object({
    // Each end is optional, and omitting one means the trail is unbounded that
    // way. A caller reading a whole history states no window rather than a
    // wide one: an upper bound taken from the caller's own clock excludes an
    // entry stamped by a database clock running ahead of it, and the reader is
    // told nothing.
    fromDate: z.coerce.date().optional(),
    toDate: inclusiveUpperBound.optional(),
    // A ceiling rather than a performance guess: a page big enough to be a bulk
    // export of the trail is a different thing to ask for than a search.
    limit: z.number().int().min(1).max(1000).default(100),
    offset: z.number().int().min(0).default(0),
    kinds: z.array(AuditTrailEntryKindSchema).min(1).optional(),
    // A list rather than one id, because a thing the trail is searched about is
    // not always one row: a young person is every record that has been merged
    // into theirs, and entries recorded against any of them are theirs.
    resourceIds: z.array(looseUuid()).min(1).optional(),
    // Narrows the ids above to one kind of thing. A search about a young person
    // means the person rows and not, say, an involvement that happens to be
    // identified by the same value.
    resourceKind: AuditTrailResourceKindSchema.optional(),
    userId: UserIdSchema.optional(),
  })
  // A reversed window matches nothing, which reads as "this person did nothing"
  // — the most misleading answer an audit search can give.
  .refine(
    (search) =>
      search.fromDate === undefined ||
      search.toDate === undefined ||
      search.fromDate <= search.toDate,
    {
      message: "fromDate must not be after toDate",
      path: ["fromDate"],
    },
  )
  // A kind of resource is a narrowing of the ids and is applied with them. A
  // search naming one alone would come back unfiltered while reading to its
  // caller as though it had been narrowed.
  .refine(
    (search) =>
      search.resourceKind === undefined || search.resourceIds !== undefined,
    {
      message: "resourceKind narrows resourceIds and cannot be given alone",
      path: ["resourceKind"],
    },
  )
  .brand<"haven-common:AuditTrailSearch">();

/**
 * What a search asks for.
 *
 * The window is compared as instants, and an explicit instant means exactly
 * itself — the one shape that reads unambiguously is not reinterpreted.
 *
 * A **date-only** `toDate` is the exception, and covers the whole of that day.
 * Read as an instant it would be midnight at the *start* of the day, so asking
 * for a window ending today would answer with nothing that happened today: the
 * bound a caller wrote to include a day would be the one that excluded it. Only
 * the upper bound needs this — a date-only `fromDate` already means the start of
 * its day, which is the whole of it.
 *
 * Both bounds fall on UTC day boundaries, because the request carries no zone to
 * resolve them in. For a caller an hour off UTC the edges are an hour out, which
 * is worth knowing and is not worth guessing a zone over.
 */
export type AuditTrailSearch = z.infer<typeof AuditTrailSearchSchema>;

/**
 * A page of entries, and how many the search matched in total — the count is
 * what tells a reader their page is a window onto more.
 */
export type AuditTrailPage = {
  entries: AuditTrailEntry[];
  count: number;
};

export type GetAuditTrailResponseUnbranded = {
  entries: AuditTrailEntryUnbranded[];
  count: number;
};

/**
 * The response as a client parses it.
 *
 * As strict as the entry schema it is built from, deliberately: a page that
 * cannot be parsed fails rather than rendering the entries that happened to fit.
 * A viewer that silently dropped an unreadable entry would show a trail with
 * gaps and no sign of them, which is worse than showing none.
 */
export const GetAuditTrailResponseSchema = z.object({
  entries: z.array(AuditTrailEntrySchema),
  count: z.number().int().nonnegative(),
}) satisfies ZodType<GetAuditTrailResponseUnbranded>;
